/**
 * 优惠计算引擎 - 拼单满减分摊
 */

/**
 * 应用满减规则：找到总金额能满足的最高满减档位
 * @param {Array} personList - [{ name, items: [{itemName, price}] }]
 * @param {Array} thresholds - [{ threshold: number, discount: number }]，按门槛升序排列
 * @returns {{ totalOriginal: number, totalDiscount: number, perPersonAllocation: Array, gapToNext: number|null }}
 */
export function applyThresholdDiscount(personList, thresholds) {
  const totalOriginal = personList.reduce((sum, p) =>
    sum + (p.items || []).reduce((s, it) => s + it.price, 0), 0)

  if (!thresholds || thresholds.length === 0) {
    return {
      totalOriginal,
      totalDiscount: 0,
      perPersonAllocation: personList.map(p => ({
        name: p.name,
        originalAmount: (p.items || []).reduce((s, it) => s + it.price, 0),
        discountAllocated: 0
      })),
      gapToNext: null
    }
  }

  // thresholds assumed sorted ascending; find the highest one totalOriginal meets
  const sorted = [...thresholds].sort((a, b) => a.threshold - b.threshold)
  let applied = null
  for (const t of sorted) {
    if (totalOriginal >= t.threshold) {
      applied = t
    } else {
      break
    }
  }

  const totalDiscount = applied ? applied.discount : 0
  const nextThreshold = sorted.find(t => t.threshold > totalOriginal)

  // 按个人原始金额比例分配优惠
  const perPersonAllocation = personList.map(p => {
    const originalAmount = (p.items || []).reduce((s, it) => s + it.price, 0)
    const ratio = totalOriginal > 0 ? originalAmount / totalOriginal : 0
    return {
      name: p.name,
      originalAmount,
      discountAllocated: parseFloat((totalDiscount * ratio).toFixed(2))
    }
  })

  return {
    totalOriginal,
    totalDiscount,
    perPersonAllocation,
    gapToNext: nextThreshold ? nextThreshold.threshold - totalOriginal : null,
    nextThreshold: nextThreshold || null
  }
}

/**
 * 应用优惠券（在满减之后叠加）
 * @param {Array} personList - [{ name, items }]
 * @param {Array} coupons - [{ name, amount, threshold, applicablePerson }]
 * @param {number} remainingTotal - 满减后的总金额
 * @returns {{ couponDiscount: number, couponAllocation: Array }}
 */
export function applyCoupons(personList, coupons, remainingTotal) {
  if (!coupons || coupons.length === 0) {
    return {
      couponDiscount: 0,
      couponAllocation: personList.map(p => ({ name: p.name, couponAllocated: 0 }))
    }
  }

  let totalCouponDiscount = 0
  const perPersonCoupon = personList.map(p => ({ name: p.name, couponAllocated: 0 }))

  for (const coupon of coupons) {
    if (!coupon.amount || coupon.amount <= 0) continue
    // 券门槛检查
    if (coupon.threshold && remainingTotal - totalCouponDiscount < coupon.threshold) continue

    totalCouponDiscount += coupon.amount

    if (coupon.applicablePerson) {
      // 指定适用人，优惠全给该人
      const entry = perPersonCoupon.find(e => e.name === coupon.applicablePerson)
      if (entry) {
        entry.couponAllocated += coupon.amount
      }
    } else {
      // 全场券，按原始金额比例分配
      const totalOriginal = personList.reduce((sum, p) =>
        sum + (p.items || []).reduce((s, it) => s + it.price, 0), 0)
      perPersonCoupon.forEach((entry, i) => {
        const original = (personList[i].items || []).reduce((s, it) => s + it.price, 0)
        const ratio = totalOriginal > 0 ? original / totalOriginal : 0
        entry.couponAllocated += parseFloat((coupon.amount * ratio).toFixed(2))
      })
    }
  }

  // 格式化
  perPersonCoupon.forEach(e => {
    e.couponAllocated = parseFloat(e.couponAllocated.toFixed(2))
  })

  return {
    couponDiscount: parseFloat(totalCouponDiscount.toFixed(2)),
    couponAllocation: perPersonCoupon
  }
}

/**
 * 按指定策略分配总折扣
 * @param {Array} personList - [{ name, items }]
 * @param {number} totalDiscount - 总优惠金额
 * @param {string} strategy - 'proportional' | 'perPerson' | 'organizerFirst'
 * @param {string} organizer - 发起人姓名（organizerFirst 策略时使用）
 * @returns {Array} [{ name, originalAmount, discountAllocated, adjustedAmount }]
 */
export function allocateDiscount(personList, totalDiscount, strategy, organizer) {
  const allocations = personList.map(p => {
    const originalAmount = (p.items || []).reduce((s, it) => s + it.price, 0)
    return { name: p.name, originalAmount, discountAllocated: 0, adjustedAmount: originalAmount }
  })

  if (totalDiscount <= 0 || personList.length === 0) return allocations

  const totalOriginal = allocations.reduce((s, a) => s + a.originalAmount, 0)
  if (totalOriginal <= 0) return allocations

  switch (strategy) {
    case 'perPerson': {
      // 人均分配
      const perPersonDiscount = totalDiscount / personList.length
      allocations.forEach(a => {
        a.discountAllocated = parseFloat(Math.min(perPersonDiscount, a.originalAmount).toFixed(2))
        a.adjustedAmount = parseFloat((a.originalAmount - a.discountAllocated).toFixed(2))
      })
      break
    }

    case 'organizerFirst': {
      // 发起人不享受优惠，其余人按比例分配
      const nonOrganizerOriginal = allocations
        .filter(a => a.name !== organizer)
        .reduce((s, a) => s + a.originalAmount, 0)

      allocations.forEach(a => {
        if (a.name === organizer || nonOrganizerOriginal <= 0) {
          a.discountAllocated = 0
        } else {
          const ratio = a.originalAmount / nonOrganizerOriginal
          a.discountAllocated = parseFloat((totalDiscount * ratio).toFixed(2))
        }
        a.adjustedAmount = parseFloat((a.originalAmount - a.discountAllocated).toFixed(2))
      })
      break
    }

    case 'proportional':
    default: {
      // 按金额比例分配
      allocations.forEach(a => {
        const ratio = a.originalAmount / totalOriginal
        a.discountAllocated = parseFloat((totalDiscount * ratio).toFixed(2))
        a.adjustedAmount = parseFloat((a.originalAmount - a.discountAllocated).toFixed(2))
      })
      break
    }
  }

  return allocations
}

/**
 * 完整的拼单满减计算
 * @param {Object} form - { personList, proxyList, discountRules }
 * @returns {{ resultList: Array, discountSummary: Object }}
 */
export function calculateWithDiscount(form) {
  const { personList, proxyList, discountRules } = form
  const rules = discountRules || {}
  const thresholds = (rules.thresholds || []).filter(t => t.threshold > 0 && t.discount > 0)
  const coupons = (rules.coupons || []).filter(c => c.amount > 0)
  const strategy = rules.strategy || 'proportional'
  const organizer = rules.organizer || ''

  // 1. 应用满减
  const thresholdResult = applyThresholdDiscount(personList, thresholds)

  // 2. 应用优惠券（在满减后的总金额基础上）
  const afterThreshold = thresholdResult.totalOriginal - thresholdResult.totalDiscount
  const couponResult = applyCoupons(personList, coupons, afterThreshold)

  const totalDiscount = thresholdResult.totalDiscount + couponResult.couponDiscount

  // 3. 分配总折扣
  const allocations = allocateDiscount(personList, totalDiscount, strategy, organizer)

  // 4. 合并代付调整
  let resultList = allocations.map((alloc, i) => {
    const person = personList[i]
    const payAmount = (person.items || []).reduce((s, it) => s + it.price, 0)
    const shouldPay = alloc.adjustedAmount / personList.length // 均摊应付
    const diffAmount = payAmount - shouldPay

    return {
      name: alloc.name,
      originalAmount: alloc.originalAmount,
      discountAllocated: alloc.discountAllocated,
      adjustedAmount: alloc.adjustedAmount,
      payAmount,
      shouldPay,
      diffAmount,
      finalDiff: diffAmount,
      type: diffAmount >= 0 ? '应退' : '应缴',
      finalAmount: Math.abs(diffAmount)
    }
  })

  // 代付调整
  if (proxyList) {
    proxyList.forEach(proxyItem => {
      if (proxyItem.proxyName.trim() && proxyItem.beProxyName.trim() && proxyItem.proxyAmount > 0) {
        const proxyPerson = resultList.find(item => item.name === proxyItem.proxyName.trim())
        const beProxyPerson = resultList.find(item => item.name === proxyItem.beProxyName.trim())
        if (proxyPerson && beProxyPerson) {
          proxyPerson.finalDiff += proxyItem.proxyAmount
          beProxyPerson.finalDiff -= proxyItem.proxyAmount
          proxyPerson.type = proxyPerson.finalDiff >= 0 ? '应退' : '应缴'
          proxyPerson.finalAmount = Math.abs(proxyPerson.finalDiff)
          beProxyPerson.type = beProxyPerson.finalDiff >= 0 ? '应退' : '应缴'
          beProxyPerson.finalAmount = Math.abs(beProxyPerson.finalDiff)
        }
      }
    })
  }

  // 格式化
  resultList = resultList.map(item => ({
    ...item,
    originalAmount: parseFloat(item.originalAmount.toFixed(2)),
    discountAllocated: parseFloat(item.discountAllocated.toFixed(2)),
    adjustedAmount: parseFloat(item.adjustedAmount.toFixed(2)),
    payAmount: parseFloat(item.payAmount.toFixed(2)),
    shouldPay: parseFloat(item.shouldPay.toFixed(2)),
    diffAmount: parseFloat(item.diffAmount.toFixed(2)),
    finalDiff: parseFloat(item.finalDiff.toFixed(2)),
    finalAmount: parseFloat(item.finalAmount.toFixed(2))
  }))

  return {
    resultList,
    discountSummary: {
      totalOriginal: thresholdResult.totalOriginal,
      thresholdDiscount: thresholdResult.totalDiscount,
      couponDiscount: couponResult.couponDiscount,
      totalDiscount,
      finalTotal: thresholdResult.totalOriginal - totalDiscount,
      strategy,
      gapToNext: thresholdResult.gapToNext,
      nextThreshold: thresholdResult.nextThreshold
    }
  }
}

/**
 * 校验优惠规则配置
 * @param {Object} discountRules
 * @returns {{ valid: boolean, message: string }}
 */
export function validateDiscountRules(discountRules) {
  const rules = discountRules || {}
  if (!rules.enabled) return { valid: true, message: '' }

  const thresholds = rules.thresholds || []
  if (thresholds.length > 0) {
    for (const t of thresholds) {
      if (t.threshold <= 0 || t.discount <= 0) {
        return { valid: false, message: '满减规则的门槛和减免金额必须大于0！' }
      }
      if (t.discount >= t.threshold) {
        return { valid: false, message: '减免金额不能大于或等于门槛金额！' }
      }
    }
  }

  if (rules.strategy === 'organizerFirst' && !rules.organizer) {
    return { valid: false, message: '发起人优先模式下必须指定发起人！' }
  }

  return { valid: true, message: '' }
}
